/**
 * アクセスルール（設定 > 認証 > アクセスルール）の Contract。**publish 前のローカル定義**。
 *
 * dresscode-backend #8514 の
 * `shared-kernel/api/settings/usecase/authentication-policy-access-rule.api-contract.ts` を写したもの。
 * publish 後はこのファイルを削除し `@standandforce/api-contract` からの import に差し替える。
 *
 * アクセスルール = 認証ポリシーの名前付きアタッチ。「誰に（PeopleGroup / アカウント種別）」
 * と「どのポリシーを」を束ね、1 ポリシーは複数アクセスルールから再利用できる。
 * デフォルトポリシーは全員対象のためアクセスルールから参照できない。
 */
import { oc } from '@orpc/contract';
// MembershipTypeSchema は既に publish 済みなので package から使う
import { MembershipTypeSchema } from '@standandforce/api-contract';
import { z } from 'zod';
import { AuthenticationPolicyStatusSchema } from './authentication-policy';
import { pageSizeAllowingAllSchema, paginatedSchema, paginationQuerySchema } from './pagination';
const assignmentTargetOptions = {
  peopleGroup: z.object({
    targetKind: z.literal('PEOPLE_GROUP'),
    /** purpose=PERMISSION_AND_ROLES のユーザー管理グループのみ指定可 */
    peopleGroupId: z.string().min(1)
  }),
  membershipType: z.object({
    targetKind: z.literal('MEMBERSHIP_TYPE'),
    membershipType: MembershipTypeSchema
  })
} as const;
export const AuthenticationPolicyAssignmentInputSchema = z.discriminatedUnion('targetKind', [assignmentTargetOptions.peopleGroup, assignmentTargetOptions.membershipType]);
export const AuthenticationPolicyAssignmentViewSchema = z.discriminatedUnion('targetKind', [assignmentTargetOptions.peopleGroup.extend({
  /** 表示用に解決した PeopleGroup 情報。削除等で解決できない場合は null */
  peopleGroup: z.object({
    name: z.string(),
    memberCount: z.number().int()
  }).nullable()
}), assignmentTargetOptions.membershipType]);
export const AuthenticationPolicyAccessRuleDraftSchema = z.object({
  name: z.string().min(1),
  /** 適用する認証ポリシー（デフォルトポリシーは指定不可） */
  policyId: z.string().min(1),
  assignments: z.array(AuthenticationPolicyAssignmentInputSchema).min(1),
  /**
   * アプリケーションスコープ（it-force の Software ID）。
   * null = すべての評価コンテキスト（ログイン + 全アプリ）に適用。
   * 非 null = 指定アプリへの Outbound SSO 評価時のみ適用。
   */
  applicationScope: z.array(z.string().min(1)).min(1).nullable()
});
export const AuthenticationPolicyAccessRuleViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  policy: z.object({
    id: z.string(),
    name: z.string(),
    status: AuthenticationPolicyStatusSchema
  }),
  assignments: z.array(AuthenticationPolicyAssignmentViewSchema),
  applicationScope: z.array(z.string()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export const authenticationPolicyAccessRuleContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: '/v2/settings/authentication-policy-access-rules'
  }).input(paginationQuerySchema(z.enum(['name', 'createdAt', 'updatedAt'])).extend({
    pageSize: pageSizeAllowingAllSchema()
  })).output(paginatedSchema(AuthenticationPolicyAccessRuleViewSchema)),
  get: oc.route({
    method: 'GET',
    path: '/v2/settings/authentication-policy-access-rules/{id}'
  }).input(z.object({
    id: z.string()
  })).output(AuthenticationPolicyAccessRuleViewSchema),
  create: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policy-access-rules'
  }).input(AuthenticationPolicyAccessRuleDraftSchema).output(AuthenticationPolicyAccessRuleViewSchema),
  update: oc.route({
    method: 'PUT',
    path: '/v2/settings/authentication-policy-access-rules/{id}'
  }).input(AuthenticationPolicyAccessRuleDraftSchema.extend({
    id: z.string()
  })).output(AuthenticationPolicyAccessRuleViewSchema),
  delete: oc.route({
    method: 'DELETE',
    path: '/v2/settings/authentication-policy-access-rules/{id}'
  }).input(z.object({
    id: z.string()
  })).output(z.object({
    id: z.string()
  }))
});
export type MembershipType = z.infer<typeof MembershipTypeSchema>;
export type AuthenticationPolicyAssignmentInput = z.infer<typeof AuthenticationPolicyAssignmentInputSchema>;
export type AuthenticationPolicyAssignmentView = z.infer<typeof AuthenticationPolicyAssignmentViewSchema>;
export type AuthenticationPolicyAccessRuleDraftInput = z.infer<typeof AuthenticationPolicyAccessRuleDraftSchema>;
export type AuthenticationPolicyAccessRuleView = z.infer<typeof AuthenticationPolicyAccessRuleViewSchema>;